import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { adminAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { AdminSidebar } from '../components/admin/AdminSidebar';
import { formatDate } from '../utils/formatDate';
import { toast } from 'sonner';
import {
  Search, Filter, Eye, LogOut,
  ChevronLeft, ChevronRight, Loader2, Building2
} from 'lucide-react';

const STATUS_CONFIG = {
  submitted: { label: 'Submitted', color: 'status-submitted' },
  under_review: { label: 'Under Review', color: 'status-under-review' },
  pending_ar: { label: 'Pending AR Review', color: 'bg-purple-100 text-purple-800' },
  pending_hos: { label: 'Pending HoS Approval', color: 'bg-indigo-100 text-indigo-800' },
  query_raised: { label: 'Query Raised', color: 'status-query-raised' },
  approved: { label: 'Approved', color: 'status-approved' },
  rejected: { label: 'Rejected', color: 'status-rejected' },
};

const DESK_STATUS_OPTIONS = {
  dealing_assistant: [
    'submitted',
    'under_review',
    'query_raised',
    'pending_ar',
    'pending_hos',
    'approved',
    'rejected',
  ],
  assistant_registrar: ['pending_ar', 'query_raised', 'pending_hos', 'approved', 'rejected'],
  head_of_stores: ['pending_hos', 'query_raised', 'approved', 'rejected'],
};

const CATEGORIES = [
  { value: 'goods', label: 'Goods' },
  { value: 'services', label: 'Services' },
  { value: 'works', label: 'Works' },
  { value: 'consultancy', label: 'Consultancy' },
];

export default function ApplicationList() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const statusOptions = DESK_STATUS_OPTIONS[user?.role] || Object.keys(STATUS_CONFIG);
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 20, total_pages: 1 });
  
  const [filters, setFilters] = useState({
    vendor_name: searchParams.get('vendor_name') || '',
    application_id: searchParams.get('application_id') || '',
    status: searchParams.get('status') || 'all',
    category: searchParams.get('category') || 'all',
    is_msme: searchParams.get('is_msme') || 'all',
    is_startup: searchParams.get('is_startup') || 'all',
  });

  const fetchApplications = useCallback(async () => {
    setLoading(true);
    try {
      // Read applied filters from URL so fetch only re-runs when searchParams change
      // (local `filters` is draft form state until Search is clicked).
      const params = {
        page: parseInt(searchParams.get('page')) || 1,
        limit: 20,
      };

      const vendor_name = searchParams.get('vendor_name');
      const application_id = searchParams.get('application_id');
      const status = searchParams.get('status');
      const category = searchParams.get('category');
      const is_msme = searchParams.get('is_msme');
      const is_startup = searchParams.get('is_startup');

      if (vendor_name) params.vendor_name = vendor_name;
      if (application_id) params.application_id = application_id;
      params.status = status || 'all';
      if (category && category !== 'all') params.category = category;
      if (is_msme === 'true') params.is_msme = true;
      if (is_startup === 'true') params.is_startup = true;

      const response = await adminAPI.getApplications(params);
      setApplications(response.data.applications);
      setPagination({
        total: response.data.total,
        page: response.data.page,
        limit: response.data.limit,
        total_pages: response.data.total_pages,
      });
    } catch (error) {
      toast.error('Failed to fetch applications');
    } finally {
      setLoading(false);
    }
  }, [searchParams]);

  useEffect(() => {
    fetchApplications();
  }, [fetchApplications]);

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) params.set(key, value);
    });
    params.set('page', '1');
    setSearchParams(params);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const applySelectFilter = (key, value) => {
    const next = { ...filters, [key]: value };
    setFilters(next);
    const params = new URLSearchParams(searchParams);
    params.set(key, value);
    params.set('page', '1');
    setSearchParams(params);
  };

  const clearFilters = () => {
    setFilters({
      vendor_name: '',
      application_id: '',
      status: 'all',
      category: 'all',
      is_msme: 'all',
      is_startup: 'all',
    });
    setSearchParams(new URLSearchParams());
  };

  const handlePageChange = (newPage) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', newPage.toString());
    setSearchParams(params);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="tricolor-strip" />
      <header className="bg-white border-b border-slate-200 shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img 
                src="/iith-logo.png" 
                alt="Emblem of India" 
                className="h-12 w-auto"
              />
              <div>
                <h1 className="text-xl font-bold govt-blue-text font-serif">Department Portal</h1>
                <p className="text-sm text-slate-600">Vendor Application Management</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="text-right mr-1">
                <p className="text-sm font-medium text-slate-800">{user?.name}</p>
                <p className="text-xs text-slate-500 capitalize">{user?.role?.replace(/_/g, ' ')}</p>
              </div>
              <Button variant="outline" onClick={handleLogout} size="sm" className="text-slate-600 border-slate-300" data-testid="logout-btn">
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="flex">
        <AdminSidebar />

        {/* Main Content */}
        <main className="flex-1 p-6">
          <div className="mb-6">
            <h2 className="text-2xl font-bold font-serif govt-blue-text">Vendor Applications</h2>
            <p className="text-slate-600">Review and manage vendor registration applications</p>
          </div>

          {/* Filters */}
          <Card className="govt-card mb-6">
            <CardContent className="p-4">
              <form onSubmit={handleSearch}>
                <div className="grid md:grid-cols-4 gap-4 mb-4">
                  <div>
                    <Input
                      placeholder="Search by vendor name..."
                      value={filters.vendor_name}
                      onChange={(e) => handleFilterChange('vendor_name', e.target.value)}
                      data-testid="filter-vendor-name"
                    />
                  </div>
                  <div>
                    <Input
                      placeholder="Application ID..."
                      value={filters.application_id}
                      onChange={(e) => handleFilterChange('application_id', e.target.value)}
                      data-testid="filter-application-id"
                    />
                  </div>
                  <div>
                    <Select value={filters.status} onValueChange={(value) => applySelectFilter('status', value)}>
                      <SelectTrigger data-testid="filter-status">
                        <SelectValue placeholder="All Status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        {statusOptions.map((key) => (
                          <SelectItem key={key} value={key}>{STATUS_CONFIG[key]?.label || key}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Select value={filters.category} onValueChange={(value) => applySelectFilter('category', value)}>
                      <SelectTrigger data-testid="filter-category">
                        <SelectValue placeholder="All Categories" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Categories</SelectItem>
                        {CATEGORIES.map(cat => (
                          <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Select value={filters.is_msme} onValueChange={(value) => applySelectFilter('is_msme', value)}>
                      <SelectTrigger className="w-32" data-testid="filter-msme">
                        <SelectValue placeholder="MSME" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        <SelectItem value="true">MSME Only</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select value={filters.is_startup} onValueChange={(value) => applySelectFilter('is_startup', value)}>
                      <SelectTrigger className="w-32" data-testid="filter-startup">
                        <SelectValue placeholder="Startup" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        <SelectItem value="true">Startups Only</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex-1" />
                  <Button type="button" variant="outline" onClick={clearFilters} data-testid="clear-filters-btn">
                    Clear Filters
                  </Button>
                  <Button type="submit" className="govt-btn-primary" data-testid="search-btn">
                    <Search className="h-4 w-4 mr-2" />
                    Search
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>

          {/* Applications Table */}
          <Card className="govt-card">
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-[#003380]" />
                </div>
              ) : applications.length === 0 ? (
                <div className="text-center py-12">
                  <Building2 className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                  <p className="text-slate-500">No applications found</p>
                </div>
              ) : (
                <>
                  <table className="govt-table">
                    <thead>
                      <tr>
                        <th>Application ID</th>
                        <th>Vendor Name</th>
                        <th>Category</th>
                        <th>Submitted On</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {applications.map((app) => (
                        <tr key={app.id}>
                          <td className="font-medium">{app.application_number}</td>
                          <td>
                            <div>
                              <p className="font-medium">{app.basic_info?.company_name || '-'}</p>
                              <p className="text-xs text-slate-500 capitalize">
                                {app.basic_info?.business_type?.replace('_', ' ')}
                              </p>
                            </div>
                          </td>
                          <td>
                            <div className="flex flex-wrap gap-1">
                              {app.capability?.categories?.map(cat => (
                                <Badge key={cat} variant="outline" className="text-xs capitalize">
                                  {cat}
                                </Badge>
                              )) || '-'}
                            </div>
                            <div className="flex gap-1 mt-1">
                              {app.capability?.is_msme && (
                                <Badge className="bg-blue-100 text-blue-700 text-xs">MSME</Badge>
                              )}
                              {app.capability?.is_startup && (
                                <Badge className="bg-purple-100 text-purple-700 text-xs">Startup</Badge>
                              )}
                            </div>
                          </td>
                          <td>
                            {formatDate(app.submitted_at)}
                          </td>
                          <td>
                            <Badge className={STATUS_CONFIG[app.status]?.color || 'status-draft'}>
                              {STATUS_CONFIG[app.status]?.label || app.status}
                            </Badge>
                          </td>
                          <td>
                            <Link to={`/admin/applications/${app.id}`}>
                              <Button variant="outline" size="sm" data-testid={`view-${app.id}-btn`}>
                                <Eye className="h-4 w-4 mr-1" />
                                View
                              </Button>
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {/* Pagination */}
                  <div className="flex items-center justify-between p-4 border-t border-slate-200">
                    <p className="text-sm text-slate-600">
                      Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
                      {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
                      {pagination.total} applications
                    </p>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(pagination.page - 1)}
                        disabled={pagination.page <= 1}
                        data-testid="prev-page-btn"
                      >
                        <ChevronLeft className="h-4 w-4" />
                        Previous
                      </Button>
                      <span className="text-sm text-slate-600">
                        Page {pagination.page} of {pagination.total_pages}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(pagination.page + 1)}
                        disabled={pagination.page >= pagination.total_pages}
                        data-testid="next-page-btn"
                      >
                        Next
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </main>
      </div>
    </div>
  );
}
